// ContaStarkBank.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import './ContaStarkBank.css';

/**
 * ContaStarkBank.js — CONSOLIDADO (v6) — modelo Banking (Possibilidade 1)
 * ---------------------------------------------------------------------------
 * Fusão do melhor de ContaStarkBank + WalletGestorBaas, tudo sobre
 * /api/baas/account/* e no design csb- (o WalletGestorBaas.js e .css foram
 * aposentados).
 *
 * Base (ContaStarkBank): wizard de conexão por Project ID, saldo real,
 *   extrato com cursor, pagar boleto, transferência, desconectar, tendência.
 * Trazido do Wallet: layout em abas (Visão Geral / Extrato / Cobranças),
 *   faixa de KPIs (créditos/débitos hoje e mês, cartões ativos) e o modal
 *   Pix com POLLING — a cobrança confirma sozinha ("Aporte confirmado!")
 *   quando o Pix cai, via /account/invoices/:id/sync, sem recarregar a tela.
 * Descartado (era do modelo interno, sem backend Banking): saldo duplo,
 *   saldo alocado por cartão, ajuste manual, transferir/recolher de cartão.
 * ---------------------------------------------------------------------------
 */

const fmtBRL = (v) =>
    (parseFloat(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const fmtDateShort = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const STATUS_LABEL = {
    created: 'Aguardando pagamento', paid: 'Paga', canceled: 'Cancelada',
    overdue: 'Expirada', expired: 'Expirada',
    success: 'Concluído', processing: 'Processando', failed: 'Falhou',
};

export default function ContaStarkBank() {
    const [loading, setLoading] = useState(true);
    const [connected, setConnected] = useState(false);
    const [account, setAccount] = useState(null);
    const [toast, setToast] = useState(null);

    const showToast = useCallback((msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    const loadStatus = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/baas/account');
            setConnected(!!data.connected);
            setAccount(data.account);
        } catch (e) {
            showToast(e.response?.data?.error || e.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    return (
        <div className="csb-wrap">
            <header className="csb-header">
                <div>
                    <h1>Conta Digital Stark Bank</h1>
                    <p className="csb-sub">Saldo, extrato, aportes, boletos e transferências da conta conectada do cliente.</p>
                </div>
            </header>

            {toast && <div className={`csb-toast csb-toast-${toast.type}`}>{toast.msg}</div>}

            {loading ? (
                <div className="csb-loading">Carregando...</div>
            ) : connected ? (
                <Dashboard account={account} showToast={showToast} onDisconnected={loadStatus} />
            ) : (
                <ConnectWizard account={account} onConnected={loadStatus} showToast={showToast} />
            )}
        </div>
    );
}

// =============================================================================
// ASSISTENTE DE CONEXÃO
// =============================================================================
function ConnectWizard({ account, onConnected, showToast }) {
    const [publicKey, setPublicKey] = useState(null);
    const [instructions, setInstructions] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [environment, setEnvironment] = useState('sandbox');
    const [connecting, setConnecting] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        api.get('/api/baas/account/public-key')
            .then(({ data }) => { setPublicKey(data.public_key); setInstructions(data.instructions || []); })
            .catch(() => {});
    }, []);

    const copyKey = () => {
        navigator.clipboard.writeText(publicKey || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleConnect = async (e) => {
        e.preventDefault();
        if (!projectId.trim()) return showToast('Informe o Project ID.', 'error');
        setConnecting(true);
        try {
            await api.post('/api/baas/account/connect', {
                stark_project_id: projectId.trim(),
                environment,
            });
            showToast('Conta conectada com sucesso!');
            onConnected();
        } catch (e2) {
            showToast(e2.response?.data?.error || e2.message, 'error');
        } finally {
            setConnecting(false);
        }
    };

    return (
        <div className="csb-wizard">
            {account?.status === 'error' && (
                <div className="csb-alert">
                    <strong>Última tentativa falhou:</strong> {account.last_error}
                </div>
            )}

            <div className="csb-wizard-grid">
                <div className="csb-step-card">
                    <span className="csb-step-num">1</span>
                    <h3>Copie a chave pública da BLB</h3>
                    <p>O cliente vai cadastrar esta chave no Web Banking da conta Stark Bank dele.</p>
                    <div className="csb-key-box">
                        <code>{publicKey ? `${publicKey.slice(0, 60)}...` : 'Carregando...'}</code>
                        <button className="csb-btn-ghost" onClick={copyKey} disabled={!publicKey}>
                            {copied ? 'Copiado!' : 'Copiar'}
                        </button>
                    </div>
                </div>

                <div className="csb-step-card">
                    <span className="csb-step-num">2</span>
                    <h3>Peça para o cliente cadastrar</h3>
                    <ol className="csb-instructions">
                        {instructions.map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                </div>

                <div className="csb-step-card">
                    <span className="csb-step-num">3</span>
                    <h3>Informe o Project ID recebido</h3>
                    <form onSubmit={handleConnect} className="csb-connect-form">
                        <label>
                            Ambiente
                            <select value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                                <option value="sandbox">Sandbox (testes)</option>
                                <option value="production">Produção</option>
                            </select>
                        </label>
                        <label>
                            Project ID
                            <input
                                type="text"
                                placeholder="Ex: 5656565656565656"
                                value={projectId}
                                onChange={(e) => setProjectId(e.target.value)}
                            />
                        </label>
                        <button type="submit" className="csb-btn-primary" disabled={connecting}>
                            {connecting ? 'Validando conexão...' : 'Testar e conectar'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// FAIXA DE KPIs (trazida do Wallet, re-vestida csb via estilo inline leve)
// =============================================================================
function KpiStrip({ items }) {
    // Estilo movido para classes .csb-kpi* (antes eram inline numa paleta teal
    // que destoava do tema navy/gold). O accent semântico de cada KPI e o índice
    // (para o stagger da animação de entrada) viajam como CSS custom properties.
    return (
        <div className="csb-kpi-strip">
            {items.map((k, i) => (
                <div
                    key={k.label}
                    className="csb-kpi"
                    style={{ '--kpi-accent': k.accent || 'var(--gold)', '--kpi-i': i }}
                >
                    <span className="csb-kpi-label">{k.label}</span>
                    <strong className="csb-kpi-value">{k.value}</strong>
                </div>
            ))}
        </div>
    );
}

// =============================================================================
// DASHBOARD (consolidado: hero + KPIs + abas)
// =============================================================================
function Dashboard({ account, showToast, onDisconnected }) {
    const [balance, setBalance] = useState(null);
    const [statement, setStatement] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loadingStatement, setLoadingStatement] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [cards, setCards] = useState([]);
    const [trend, setTrend] = useState([]);

    const [tab, setTab] = useState('overview');

    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showBoletoModal, setShowBoletoModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);

    const loadBalance = useCallback(async () => {
        try {
            const { data } = await api.get('/api/baas/account/balance');
            setBalance(data);
        } catch (e) {
            showToast(e.response?.data?.error || e.message, 'error');
        }
    }, [showToast]);

    const loadStatement = useCallback(async (nextCursor = null) => {
        setLoadingStatement(true);
        try {
            const params = { limit: 20, ...(nextCursor ? { cursor: nextCursor } : {}) };
            const { data } = await api.get('/api/baas/account/statement', { params });
            setStatement((prev) => nextCursor ? [...prev, ...data.transactions] : data.transactions);
            setCursor(data.cursor);
        } catch (e) {
            showToast(e.response?.data?.error || e.message, 'error');
        } finally {
            setLoadingStatement(false);
        }
    }, [showToast]);

    const loadTrend = useCallback(async () => {
        try {
            const after = new Date();
            after.setDate(after.getDate() - 14);
            const { data } = await api.get('/api/baas/account/statement', {
                params: { limit: 100, after: after.toISOString().slice(0, 10) },
            });
            const byDay = {};
            (data.transactions || []).forEach((t) => {
                const day = fmtDateShort(t.created);
                if (!byDay[day]) byDay[day] = { day, in: 0, out: 0 };
                if (t.amount >= 0) byDay[day].in += t.amount;
                else byDay[day].out += Math.abs(t.amount);
            });
            setTrend(Object.values(byDay).slice(-14));
        } catch {
            // gráfico é bônus visual — falha silenciosa não trava a tela
        }
    }, []);

    const loadInvoices = useCallback(async () => {
        setLoadingInvoices(true);
        try {
            const { data } = await api.get('/api/baas/account/invoices');
            setInvoices(data);
        } catch (e) {
            showToast(e.response?.data?.error || e.message, 'error');
        } finally {
            setLoadingInvoices(false);
        }
    }, [showToast]);

    const loadCards = useCallback(async () => {
        try {
            const { data } = await api.get('/api/baas/account/cards');
            setCards(data || []);
        } catch {
            // usado só para KPI de cartões ativos
        }
    }, []);

    useEffect(() => {
        loadBalance(); loadStatement(); loadTrend(); loadInvoices(); loadCards();
    }, [loadBalance, loadStatement, loadTrend, loadInvoices, loadCards]);

    const syncInvoice = async (id) => {
        try {
            const { data } = await api.post(`/api/baas/account/invoices/${id}/sync`);
            setInvoices((prev) => prev.map((inv) => inv.id === id ? { ...inv, status: data.status } : inv));
            showToast('Status atualizado!');
        } catch (e) {
            showToast(e.response?.data?.error || e.message, 'error');
        }
    };

    const exportCSV = () => {
        const header = 'Data,Descricao,Origem,Valor\n';
        const rows = statement.map((t) =>
            `"${fmtDate(t.created)}","${(t.description || '').replace(/"/g, '""')}","${t.source || ''}",${t.amount}`
        ).join('\n');
        const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `extrato-stark-bank-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    // ── KPIs derivados do extrato + cartões ─────────────────────────────────
    const now = new Date();
    const isToday = (iso) => { const d = new Date(iso); return d.toDateString() === now.toDateString(); };
    const isThisMonth = (iso) => { const d = new Date(iso); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); };

    const creditosHoje = statement.filter((t) => t.amount > 0 && isToday(t.created)).reduce((s, t) => s + t.amount, 0);
    const debitosHoje = statement.filter((t) => t.amount < 0 && isToday(t.created)).reduce((s, t) => s + Math.abs(t.amount), 0);
    const creditosMes = statement.filter((t) => t.amount > 0 && isThisMonth(t.created)).reduce((s, t) => s + t.amount, 0);
    const debitosMes = statement.filter((t) => t.amount < 0 && isThisMonth(t.created)).reduce((s, t) => s + Math.abs(t.amount), 0);
    const cartoesAtivos = cards.filter((c) => c.status === 'active').length;

    const kpis = [
        { label: 'Créditos hoje', value: fmtBRL(creditosHoje), accent: '#10b981' },
        { label: 'Débitos hoje', value: fmtBRL(debitosHoje), accent: '#ef4444' },
        { label: 'Créditos no mês', value: fmtBRL(creditosMes), accent: '#3b82f6' },
        { label: 'Débitos no mês', value: fmtBRL(debitosMes), accent: '#f59e0b' },
        { label: 'Cartões ativos', value: String(cartoesAtivos), accent: '#7c3aed' },
        { label: 'Saldo disponível', value: balance ? fmtBRL(balance.amount) : '—', accent: '#0d7377' },
    ];

    return (
        <div className="csb-dashboard">
            {/* ── COLUNA ESQUERDA — saldo, ações, tendência ─────────────── */}
            <div className="csb-col-left">
                <div className="csb-balance-card">
                    <div className="csb-balance-top">
                        <span className={`csb-badge ${account.environment === 'production' ? 'csb-badge--prod' : 'csb-badge--sbx'}`}>{account.environment === 'production' ? 'Produção' : 'Sandbox'}</span>
                        <button className="csb-btn-ghost-sm" onClick={loadBalance}>Atualizar</button>
                    </div>
                    <span className="csb-balance-eyebrow">Conta Digital · Stark Bank</span>
                    <span className="csb-balance-label">Saldo disponível</span>
                    <span className="csb-balance-value">{balance ? fmtBRL(balance.amount) : '—'}</span>
                    <span className="csb-balance-updated">
                        {balance ? `Atualizado em ${fmtDate(balance.updated)}` : ''}
                    </span>
                    <button className="csb-btn-primary csb-balance-cta" onClick={() => setShowInvoiceModal(true)}>
                        Aportar via Pix
                    </button>
                    <button className="csb-link-danger" onClick={() => setShowDisconnectModal(true)}>
                        Desconectar / trocar de ambiente
                    </button>
                </div>

                <RendaFixaCard />

                <WebhookCard />

                <div className="csb-quick-actions">
                    <button className="csb-quick-btn" onClick={() => setShowBoletoModal(true)}>
                        <span className="csb-quick-icon">🧾</span>
                        <span>Pagar boleto</span>
                    </button>
                    <button className="csb-quick-btn" onClick={() => setShowTransferModal(true)}>
                        <span className="csb-quick-icon">↔️</span>
                        <span>Transferência</span>
                    </button>
                </div>

                {trend.length > 0 && <TrendChart data={trend} />}
            </div>

            {/* ── COLUNA DIREITA — KPIs + abas ──────────────────────────── */}
            <div className="csb-col-right">
                <KpiStrip items={kpis} />

                <div className="csb-tabs">
                    <button className={`csb-tab ${tab === 'overview' ? 'csb-tab--active' : ''}`} onClick={() => setTab('overview')}>Visão Geral</button>
                    <button className={`csb-tab ${tab === 'statement' ? 'csb-tab--active' : ''}`} onClick={() => setTab('statement')}>Extrato</button>
                    <button className={`csb-tab ${tab === 'invoices' ? 'csb-tab--active' : ''}`} onClick={() => setTab('invoices')}>Cobranças</button>
                </div>

                {/* ── VISÃO GERAL ── */}
                {tab === 'overview' && (
                    <div className="csb-statement-card">
                        <div className="csb-card-head">
                            <h3>Últimas movimentações</h3>
                        </div>
                        {statement.length === 0 && !loadingStatement && (
                            <p className="csb-empty">Nenhuma movimentação encontrada ainda.</p>
                        )}
                        {statement.slice(0, 8).map((t) => (
                            <div key={t.id} className="csb-invoice-row">
                                <div className="csb-invoice-row-main">
                                    <strong className={t.amount < 0 ? 'csb-neg' : 'csb-pos'}>
                                        {t.amount < 0 ? '− ' : '+ '}{fmtBRL(Math.abs(t.amount))}
                                    </strong>
                                    <span className="csb-invoice-row-name">{t.description || 'Movimentação'}</span>
                                    <span className="csb-invoice-row-date">{t.source || 'Stark Bank'} · {fmtDate(t.created)}</span>
                                </div>
                            </div>
                        ))}
                        {statement.length > 8 && (
                            <button className="csb-btn-ghost" onClick={() => setTab('statement')}>Ver extrato completo</button>
                        )}
                    </div>
                )}

                {/* ── EXTRATO ── */}
                {tab === 'statement' && (
                    <div className="csb-statement-card">
                        <div className="csb-card-head">
                            <h3>Extrato</h3>
                            {statement.length > 0 && (
                                <button className="csb-btn-ghost-sm" onClick={exportCSV}>Exportar CSV</button>
                            )}
                        </div>
                        {statement.length === 0 && !loadingStatement && (
                            <p className="csb-empty">Nenhuma movimentação encontrada ainda.</p>
                        )}
                        {statement.length > 0 && (
                            <table className="csb-table">
                                <thead>
                                    <tr><th>Data</th><th>Descrição</th><th>Origem</th><th>Valor</th></tr>
                                </thead>
                                <tbody>
                                    {statement.map((t) => (
                                        <tr key={t.id}>
                                            <td>{fmtDate(t.created)}</td>
                                            <td>{t.description || '—'}</td>
                                            <td>{t.source || '—'}</td>
                                            <td className={t.amount < 0 ? 'csb-neg' : 'csb-pos'}>{fmtBRL(t.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {cursor && (
                            <button className="csb-btn-ghost" disabled={loadingStatement} onClick={() => loadStatement(cursor)}>
                                {loadingStatement ? 'Carregando...' : 'Carregar mais'}
                            </button>
                        )}
                    </div>
                )}

                {/* ── COBRANÇAS ── */}
                {tab === 'invoices' && (
                    <div className="csb-invoices-card">
                        <h3>Histórico de cobranças Pix</h3>
                        {invoices.length === 0 && !loadingInvoices && (
                            <p className="csb-empty">Nenhuma cobrança gerada ainda.</p>
                        )}
                        <div className="csb-invoice-list">
                            {invoices.map((inv) => (
                                <div key={inv.id} className="csb-invoice-row">
                                    <div className="csb-invoice-row-main">
                                        <strong>{fmtBRL(inv.amount)}</strong>
                                        <span className="csb-invoice-row-name">{inv.name}</span>
                                        <span className="csb-invoice-row-date">{fmtDate(inv.created_at)}</span>
                                    </div>
                                    <div className="csb-invoice-row-actions">
                                        <span className={`csb-status-pill csb-status-${inv.status}`}>
                                            {STATUS_LABEL[inv.status] || inv.status}
                                        </span>
                                        <button className="csb-btn-ghost-sm" onClick={() => syncInvoice(inv.id)}>Sincronizar</button>
                                        {inv.pdf && (
                                            <a href={inv.pdf} target="_blank" rel="noreferrer" className="csb-btn-ghost-sm">PDF</a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {showInvoiceModal && (
                <InvoiceModal
                    onClose={() => setShowInvoiceModal(false)}
                    onSettled={() => { loadInvoices(); loadBalance(); loadStatement(); }}
                    showToast={showToast}
                />
            )}
            {showBoletoModal && (
                <BoletoPaymentModal onClose={() => setShowBoletoModal(false)} showToast={showToast} onPaid={loadBalance} />
            )}
            {showTransferModal && (
                <TransferModal onClose={() => setShowTransferModal(false)} showToast={showToast} onSent={loadBalance} />
            )}
            {showDisconnectModal && (
                <DisconnectModal
                    onClose={() => setShowDisconnectModal(false)}
                    onConfirm={async () => {
                        try {
                            await api.delete('/api/baas/account');
                            showToast('Conta desconectada.');
                            onDisconnected();
                        } catch (e) {
                            showToast(e.response?.data?.error || e.message, 'error');
                        }
                    }}
                />
            )}
        </div>
    );
}

// =============================================================================
// MINI GRÁFICO — ENTRADAS x SAÍDAS (14 DIAS)
// =============================================================================
function TrendChart({ data }) {
    const max = Math.max(1, ...data.flatMap((d) => [d.in, d.out]));
    const W = 280, H = 120, barW = Math.min(14, (W / data.length) / 2 - 2);

    return (
        <div className="csb-trend-card">
            <h3>Entradas x Saídas (14 dias)</h3>
            <svg viewBox={`0 0 ${W} ${H}`} className="csb-trend-svg" preserveAspectRatio="none">
                {data.map((d, i) => {
                    const groupW = W / data.length;
                    const xIn = i * groupW + groupW / 2 - barW - 1;
                    const xOut = i * groupW + groupW / 2 + 1;
                    const hIn = (d.in / max) * (H - 20);
                    const hOut = (d.out / max) * (H - 20);
                    return (
                        <g key={d.day}>
                            <rect x={xIn} y={H - hIn - 14} width={barW} height={hIn} className="csb-trend-bar-in" />
                            <rect x={xOut} y={H - hOut - 14} width={barW} height={hOut} className="csb-trend-bar-out" />
                            <text x={i * groupW + groupW / 2} y={H - 2} textAnchor="middle" className="csb-trend-label">{d.day}</text>
                        </g>
                    );
                })}
            </svg>
            <div className="csb-trend-legend">
                <span><i className="csb-dot csb-dot-in" /> Entradas</span>
                <span><i className="csb-dot csb-dot-out" /> Saídas</span>
            </div>
        </div>
    );
}

// =============================================================================
// MODAL — GERAR COBRANÇA PIX (com POLLING de confirmação — trazido do Wallet)
// =============================================================================
function InvoiceModal({ onClose, onSettled, showToast }) {
    const [amount, setAmount] = useState('');
    const [name, setName] = useState('');
    const [taxId, setTaxId] = useState('');
    const [description, setDescription] = useState('');
    const [creating, setCreating] = useState(false);
    const [pix, setPix] = useState(null); // { id, starkInvoiceId, brcode, amount, pdf, status, startedAt }
    const pollRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post('/api/baas/account/invoice', {
                amount, name, tax_id: taxId, description,
            });
            setPix({
                starkInvoiceId: data.stark_invoice_id ?? null,
                brcode: data.brcode,
                amount: data.amount,
                pdf: data.pdf,
                status: 'pending',
                startedAt: Date.now(),
            });
            onSettled();
        } catch (e2) {
            showToast(e2.response?.data?.error || e2.message, 'error');
        } finally {
            setCreating(false);
        }
    };

    // ── Polling: confirma o aporte sozinho quando o Pix cai ─────────────────
    //   O POST /invoice NÃO devolve o id local (só stark_invoice_id/brcode).
    //   E o GET /invoices devolve o status ARMAZENADO, que só muda via /sync.
    //   Então a cada tick: acha a cobrança na lista (por stark_invoice_id/brcode)
    //   -> pega o id local dela -> chama /sync pra forçar o status fresco da Stark.
    useEffect(() => {
        if (!pix || pix.status !== 'pending') return undefined;

        pollRef.current = setInterval(async () => {
            if (Date.now() - pix.startedAt > 10 * 60 * 1000) {
                clearInterval(pollRef.current);
                return;
            }
            try {
                const { data } = await api.get('/api/baas/account/invoices');
                const found = (data || []).find((i) =>
                    (pix.starkInvoiceId && i.stark_invoice_id === pix.starkInvoiceId) ||
                    (pix.brcode && i.brcode === pix.brcode)
                );
                if (!found) return;
                if (found.status === 'paid') {
                    setPix((p) => (p ? { ...p, status: 'paid' } : p));
                    onSettled();
                    return;
                }
                const { data: s } = await api.post(`/api/baas/account/invoices/${found.id}/sync`);
                if (s?.status === 'paid') {
                    setPix((p) => (p ? { ...p, status: 'paid' } : p));
                    onSettled();
                }
            } catch {
                // silencioso — tenta de novo no próximo tick
            }
        }, 6000);

        return () => clearInterval(pollRef.current);
    }, [pix, onSettled]);

    const copyBrcode = () => {
        navigator.clipboard.writeText(pix?.brcode || '');
        showToast('Código Pix copiado!');
    };

    return (
        <ModalShell onClose={onClose}>
            {!pix ? (
                <>
                    <h3>Aportar via Pix</h3>
                    <p className="csb-modal-sub">Gera uma cobrança Pix (CorporateInvoice). O saldo cai automaticamente após o pagamento.</p>
                    <form onSubmit={handleSubmit} className="csb-connect-form">
                        <label>Valor (R$)
                            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
                        </label>
                        <label>Nome do pagador
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
                        </label>
                        <label>CPF/CNPJ do pagador
                            <input type="text" value={taxId} onChange={(e) => setTaxId(e.target.value)} required />
                        </label>
                        <label>Descrição (opcional)
                            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
                        </label>
                        <button type="submit" className="csb-btn-primary" disabled={creating}>
                            {creating ? 'Gerando...' : 'Gerar cobrança Pix'}
                        </button>
                    </form>
                </>
            ) : pix.status === 'paid' ? (
                <div className="csb-invoice-result">
                    <h3>Aporte confirmado! ✓</h3>
                    <span className="csb-balance-value csb-invoice-amount">{fmtBRL(pix.amount)}</span>
                    <p className="csb-modal-sub">O saldo já foi creditado na Conta Digital. Pode fechar esta janela.</p>
                    <button className="csb-btn-primary" onClick={onClose}>Fechar</button>
                </div>
            ) : (
                <div className="csb-invoice-result">
                    <h3>Pague o Pix para concluir</h3>
                    <span className="csb-balance-value csb-invoice-amount">{fmtBRL(pix.amount)}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', margin: '8px 0', color: '#f59e0b', fontSize: 13 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', display: 'inline-block', animation: 'csbPulse 1.2s infinite' }} />
                        Aguardando pagamento — confirma sozinho aqui
                    </div>
                    <div className="csb-key-box">
                        <code>{(pix.brcode || '').slice(0, 50)}...</code>
                        <button className="csb-btn-ghost" onClick={copyBrcode}>Copiar Pix Copia e Cola</button>
                    </div>
                    {pix.pdf && (
                        <a href={pix.pdf} target="_blank" rel="noreferrer" className="csb-btn-ghost csb-link-center">
                            Abrir PDF da cobrança
                        </a>
                    )}
                    <p className="csb-modal-sub">Pague pelo app do seu banco. Não precisa recarregar a página — o saldo aparece aqui automaticamente.</p>
                    <button className="csb-link-danger" onClick={onClose}>Fechar (segue conferindo em segundo plano)</button>
                </div>
            )}
        </ModalShell>
    );
}

// =============================================================================
// MODAL — PAGAR BOLETO
// =============================================================================
function BoletoPaymentModal({ onClose, showToast, onPaid }) {
    const [line, setLine] = useState('');
    const [description, setDescription] = useState('');
    const [taxId, setTaxId] = useState('');
    const [amount, setAmount] = useState('');
    const [paying, setPaying] = useState(false);
    const [result, setResult] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setPaying(true);
        try {
            const { data } = await api.post('/api/baas/account/boleto-payment', {
                line, description, tax_id: taxId, amount: amount || undefined,
            });
            setResult(data);
            onPaid();
        } catch (e2) {
            showToast(e2.response?.data?.error || e2.message, 'error');
        } finally {
            setPaying(false);
        }
    };

    return (
        <ModalShell onClose={onClose}>
            {!result ? (
                <>
                    <h3>Pagar boleto</h3>
                    <p className="csb-modal-sub">Debita do saldo da conta do cliente conectada.</p>
                    <form onSubmit={handleSubmit} className="csb-connect-form">
                        <label>Linha digitável
                            <input type="text" value={line} onChange={(e) => setLine(e.target.value)} placeholder="00000.00000 00000.000000 00000.000000 0 00000000000000" required />
                        </label>
                        <label>Descrição (mín. 10 caracteres)
                            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} required />
                        </label>
                        <label>CPF/CNPJ do beneficiário
                            <input type="text" value={taxId} onChange={(e) => setTaxId(e.target.value)} required />
                        </label>
                        <label>Valor — deixe em branco para usar o valor do boleto
                            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        </label>
                        <button type="submit" className="csb-btn-primary" disabled={paying}>
                            {paying ? 'Processando...' : 'Pagar boleto'}
                        </button>
                    </form>
                </>
            ) : (
                <div className="csb-invoice-result">
                    <h3>Pagamento solicitado!</h3>
                    <span className="csb-balance-value csb-invoice-amount">{fmtBRL(result.amount)}</span>
                    <span className={`csb-status-pill csb-status-${result.status}`}>{STATUS_LABEL[result.status] || result.status}</span>
                </div>
            )}
        </ModalShell>
    );
}

// =============================================================================
// MODAL — TRANSFERÊNCIA AVULSA
// =============================================================================
function TransferModal({ onClose, showToast, onSent }) {
    const [form, setForm] = useState({
        amount: '', name: '', taxId: '', bankCode: '', branchCode: '', accountNumber: '', accountType: 'checking', description: '',
    });
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const { data } = await api.post('/api/baas/account/transfer', {
                amount: form.amount, name: form.name, tax_id: form.taxId,
                bank_code: form.bankCode, branch_code: form.branchCode,
                account_number: form.accountNumber, account_type: form.accountType,
                description: form.description,
            });
            setResult(data);
            onSent();
        } catch (e2) {
            showToast(e2.response?.data?.error || e2.message, 'error');
        } finally {
            setSending(false);
        }
    };

    return (
        <ModalShell onClose={onClose}>
            {!result ? (
                <>
                    <h3>Transferência (TED/Pix)</h3>
                    <p className="csb-modal-sub">Use o ISPB (8 dígitos) da instituição para enviar via Pix, ou o código bancário tradicional para TED.</p>
                    <form onSubmit={handleSubmit} className="csb-connect-form">
                        <label>Valor (R$)
                            <input type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required />
                        </label>
                        <label>Nome do recebedor
                            <input type="text" value={form.name} onChange={set('name')} required />
                        </label>
                        <label>CPF/CNPJ do recebedor
                            <input type="text" value={form.taxId} onChange={set('taxId')} required />
                        </label>
                        <label>Código do banco (ISPB p/ Pix ou código TED)
                            <input type="text" value={form.bankCode} onChange={set('bankCode')} required />
                        </label>
                        <label>Agência
                            <input type="text" value={form.branchCode} onChange={set('branchCode')} required />
                        </label>
                        <label>Conta
                            <input type="text" value={form.accountNumber} onChange={set('accountNumber')} required />
                        </label>
                        <label>Tipo de conta
                            <select value={form.accountType} onChange={set('accountType')}>
                                <option value="checking">Corrente</option>
                                <option value="savings">Poupança</option>
                                <option value="salary">Salário</option>
                                <option value="payment">Pagamento</option>
                            </select>
                        </label>
                        <label>Descrição (opcional)
                            <input type="text" value={form.description} onChange={set('description')} />
                        </label>
                        <button type="submit" className="csb-btn-primary" disabled={sending}>
                            {sending ? 'Enviando...' : 'Transferir'}
                        </button>
                    </form>
                </>
            ) : (
                <div className="csb-invoice-result">
                    <h3>Transferência solicitada!</h3>
                    <span className="csb-balance-value csb-invoice-amount">{fmtBRL(result.amount)}</span>
                    <span className={`csb-status-pill csb-status-${result.status}`}>{STATUS_LABEL[result.status] || result.status}</span>
                </div>
            )}
        </ModalShell>
    );
}

// =============================================================================
// MODAL — DESCONECTAR / TROCAR DE AMBIENTE
// =============================================================================
function DisconnectModal({ onClose, onConfirm }) {
    const [confirming, setConfirming] = useState(false);
    return (
        <ModalShell onClose={onClose}>
            <h3>Desconectar conta</h3>
            <p className="csb-modal-sub">
                Isso remove a conexão atual (Project ID e ambiente). Você poderá reconectar em Sandbox
                ou Produção logo em seguida, sem perder o histórico de cobranças já geradas.
            </p>
            <div className="csb-modal-actions">
                <button className="csb-btn-ghost" onClick={onClose}>Cancelar</button>
                <button
                    className="csb-btn-danger"
                    disabled={confirming}
                    onClick={async () => { setConfirming(true); await onConfirm(); }}
                >
                    {confirming ? 'Desconectando...' : 'Desconectar'}
                </button>
            </div>
        </ModalShell>
    );
}

// =============================================================================
// SHELL DE MODAL REUTILIZÁVEL
// =============================================================================
function ModalShell({ onClose, children, wide = false }) {
    return (
        <div className="csb-modal-overlay" onClick={onClose}>
            <div className={`csb-modal ${wide ? 'csb-modal--wide' : ''}`} onClick={(e) => e.stopPropagation()}>
                <button className="csb-modal-close" onClick={onClose} aria-label="Fechar">×</button>
                {children}
            </div>
        </div>
    );
}
// =============================================================================
// RENDA FIXA DIGITAL â valor investido + uso do limite (100% automatico)
// -----------------------------------------------------------------------------
// Le GET /api/baas/account/corporate-balance. No modelo Banking o limite do
// cartao e atrelado 1:1 a renda fixa, entao "valor investido" == limite total
// (campo `total`); `utilizado` vem do amount consumido e `disponivel` = total -
// utilizado. Atualiza sozinho quando voce aplica/resgata no Internet Banking.
// Falha graciosa: se o endpoint ainda nao existir, mostra estado neutro.
// =============================================================================
function RendaFixaCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(false);

    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const { data: d } = await api.get('/api/baas/account/corporate-balance');
                if (!vivo) return;
                setData({
                    total: Number(d?.total) || 0,
                    utilizado: Number(d?.utilizado) || 0,
                    disponivel: d?.disponivel != null ? Number(d.disponivel) : null,
                });
            } catch {
                if (vivo) setErro(true);
            } finally {
                if (vivo) setLoading(false);
            }
        })();
        return () => { vivo = false; };
    }, []);

    const total = data?.total || 0;
    const utilizado = data?.utilizado || 0;
    const disponivel = data?.disponivel != null ? data.disponivel : Math.max(0, total - utilizado);
    const pctUsado = total > 0 ? Math.min(100, Math.max(0, Math.round((utilizado / total) * 100))) : 0;
    const temValor = !erro && total > 0;

    return (
        <div className="csb-inv-card">
            <span className="csb-inv-eyebrow">Renda Fixa Digital · Stark Bank</span>

            <span className="csb-inv-label">Valor investido</span>
            {loading ? (
                <span className="csb-inv-value csb-inv-value--muted">Carregando…</span>
            ) : temValor ? (
                <span className="csb-inv-value">{fmtBRL(total)}</span>
            ) : (
                <span className="csb-inv-value csb-inv-value--muted">Sem investimento ativo</span>
            )}

            {temValor && (
                <div className="csb-inv-usage">
                    <div
                        className="csb-inv-bar"
                        role="img"
                        aria-label={`Utilizado ${fmtBRL(utilizado)} de ${fmtBRL(total)}`}
                    >
                        <span className="csb-inv-bar-fill" style={{ width: `${pctUsado}%` }} />
                    </div>
                    <div className="csb-inv-usage-legend">
                        <span><i className="csb-inv-dot csb-inv-dot--used" />Utilizado <strong>{fmtBRL(utilizado)}</strong></span>
                        <span className="csb-inv-usage-avail">Disponível <strong>{fmtBRL(disponivel)}</strong></span>
                    </div>
                </div>
            )}

            <ul className="csb-inv-facts">
                <li><span>Rentabilidade</span><strong>100% CDI</strong></li>
                <li><span>Resgate</span><strong>D+0 (mesmo dia)</strong></li>
                <li><span>Janela</span><strong>Dias úteis, 10h–16h</strong></li>
            </ul>

            <p className="csb-inv-note">
                Atualizado automaticamente pelo limite corporativo (limite = investido).
                Rendimento e IR ficam no Internet Banking da Stark.
            </p>
        </div>
    );
}

// =============================================================================
// WEBHOOK â registro/estado do webhook bancario na Stark (observe-only)
// -----------------------------------------------------------------------------
// Espelha o estado real: consulta GET /webhook no mount e mostra "ativo" com o
// ID, ou o botao de registrar. Permite remover. O registro e idempotente no
// backend (remove o anterior antes de criar), entao nao gera duplicado.
// Usa o `api` do app, que ja envia o x-csrf-token.
// =============================================================================
function WebhookCard() {
    const [estado, setEstado] = useState('loading'); // loading | none | active | error
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [erro, setErro] = useState(null);

    const carregar = useCallback(async () => {
        try {
            const { data } = await api.get('/api/baas/account/webhook');
            setInfo(data?.registered ? data : null);
            setEstado(data?.registered ? 'active' : 'none');
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
            setEstado('error');
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const acao = useCallback(async (fn) => {
        setBusy(true);
        setErro(null);
        try {
            await fn();
            await carregar();
        } catch (e) {
            setErro(e.response?.data?.error || e.message);
        } finally {
            setBusy(false);
        }
    }, [carregar]);

    const registrar = () => acao(() => api.post('/api/baas/account/webhook/registrar', {
        subscriptions: ['deposit', 'transfer', 'invoice', 'boleto-payment'],
    }));
    const remover = () => acao(() => api.delete('/api/baas/account/webhook'));

    const nSubs = Array.isArray(info?.subscriptions) ? info.subscriptions.length : null;

    return (
        <div className="csb-hook-card">
            <span className="csb-hook-eyebrow">Integração · Webhook Stark Bank</span>

            {estado === 'active' ? (
                <>
                    <div className="csb-hook-ok">
                        <span className="csb-hook-ok-badge">✓ Webhook ativo</span>
                        {info?.stark_webhook_id && <span className="csb-hook-id">ID {info.stark_webhook_id}</span>}
                    </div>
                    <p className="csb-hook-desc">
                        Recebe em tempo real as movimentações bancárias{nSubs ? ` (${nSubs} assinaturas)` : ''}:
                        Pix, transferências, cobranças e boletos.
                    </p>
                    <button className="csb-hook-remove" onClick={remover} disabled={busy}>
                        {busy ? 'Removendo…' : 'Remover webhook'}
                    </button>
                </>
            ) : (
                <>
                    <p className="csb-hook-desc">
                        Ative o recebimento em tempo real das movimentações bancárias desta conta —
                        Pix, transferências, cobranças e boletos.
                    </p>
                    <button className="csb-btn-primary csb-hook-btn" onClick={registrar} disabled={busy || estado === 'loading'}>
                        {estado === 'loading' ? 'Verificando…' : busy ? 'Registrando…' : 'Registrar webhook'}
                    </button>
                </>
            )}

            {erro && <span className="csb-hook-err">{erro}</span>}
        </div>
    );
}